/**
 * The audience-filter builder's data layer: the saved filters and the field
 * registry for the screen, and a live preview for the rules being edited.
 *
 * Hand-rolled useState/useEffect, matching `useBroadcastList.js` and
 * `useBroadcastEstimate.js` and the rest of the comms surface — this app
 * carries neither react-query nor SWR, and a hook is not the place to add a
 * dependency. The fetch idiom is the same one: a cancelled flag owned by the
 * effect plus a generation counter in a ref, combined into one `isStale()`
 * gate a response must pass before it is believed.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE OWNS AND WHAT IT DOES NOT
 * ---------------------------------------------------------------------------
 * It owns *when* to ask and *what a failure means*. It owns no wire names —
 * the response shape comes from `broadcastFilterDecode.js`, the request shape
 * is assembled inside `broadcastsService.js` from camelCase options, and the
 * copy for a failure comes from `broadcastErrors.js`. Callers receive decoded
 * camelCase objects and a human string, never a raw payload and never an
 * `err.message`.
 *
 * ---------------------------------------------------------------------------
 * THE TWO RULES THIS FILE EXISTS TO PROTECT
 * ---------------------------------------------------------------------------
 * 1. AN EMPTY RULE LIST IS NOT A QUESTION WORTH ASKING.
 *
 *    `POST /filters/preview` answers 422 for zero rules, by design: the empty
 *    list is not "everyone" (the `all` segment is how you say that), it is an
 *    author who has opened the builder and not typed anything yet. Firing the
 *    request anyway costs a round trip to be told something we already know,
 *    and puts a red error under a form nobody has filled in. So the preview
 *    hook skips the call entirely below `MIN_FILTER_RULES` and reports
 *    `preview: null` — the same "nothing to say yet" it reports before the
 *    first response lands.
 *
 * 2. A FAILED PREVIEW YIELDS `null`, NEVER ZEROS.
 *
 *    Inherited verbatim from `useBroadcastEstimate.js`, and load-bearing for
 *    the same reason. "0 people match these rules" is a finding an author will
 *    act on by rewriting a filter that was never broken. "We could not check"
 *    is a different sentence and must stay one, so a rejection sets `preview`
 *    to null and `error` to a string, and nothing here ever substitutes a
 *    number for an unknown. A genuine `matched: 0` from the server is a real
 *    preview object with `error` null, and the two must stay distinguishable.
 *
 * ---------------------------------------------------------------------------
 * THE SAVED LIST AND THE REGISTRY ARE FETCHED ONCE PER SESSION
 * ---------------------------------------------------------------------------
 * `FilterPicker` is a container, and it is mounted and unmounted constantly:
 * it appears only under the `filtered` segment, so every segment click, every
 * popup/email switch and every return to the composer used to cost another
 * `GET /filters` AND another `GET /filters/fields` — two requests for two
 * answers that had not changed, on a screen where the author is clicking
 * quickly through segments to compare their counts.
 *
 * So the load lives in a MODULE-LEVEL CACHE keyed by company id rather than in
 * component state. Consumers read it synchronously when it is warm (no
 * spinner, no flash on remount), and concurrent consumers share ONE in-flight
 * promise rather than racing two identical requests. A mutation is the only
 * thing that invalidates it, and it invalidates it for every mounted consumer
 * at once — a filter created in the picker cannot leave a stale list behind
 * somewhere else.
 *
 * A FAILED load is never cached. Caching it would make one bad response stick
 * for the rest of the session with no way to retry short of a reload.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  get as commsGet,
  post as commsPost,
  patch as commsPatch,
  remove as commsRemove,
} from "../../../service/comms/commsApi";
import {
  listFilters,
  listFilterFields,
  createFilter,
  updateFilter,
  deleteFilter,
  previewFilter,
} from "../../../service/comms/broadcastsService";
import { useCompanyId } from "../../../hooks/comms/useCompanyId";
import {
  filtersFromPayload,
  fieldsFromPayload,
  filterFromRecord,
  previewFromPayload,
} from "./broadcastFilterDecode";
import { MIN_FILTER_RULES } from "./broadcastEnums";
import { messageFor } from "./broadcastErrors";

/**
 * Debounce for the live preview, in ms.
 *
 * Longer than the composer's 300ms audience estimate on purpose. A segment is
 * one click; a rule is a field, an operator and a value, and the middle of
 * typing "45" into a `gte` is a rule that says something the author does not
 * mean. 400ms is long enough to swallow that and short enough that the number
 * still feels attached to the edit that produced it.
 */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * The `post` verb resolves `{ data, status }` where `get` and `patch` resolve
 * the body. The decoders want the body, so unwrap here rather than teaching
 * every decoder about an envelope only half the verbs produce.
 *
 * @param {Object} response
 * @returns {Object}
 */
const bodyOf = (response) =>
  response?.data !== undefined && response?.data !== null ? response.data : response;

/* ── The session cache ────────────────────────────────────────────────── */

/** What one settled load looks like. Shared by the cache and the hook. */
const EMPTY_LOAD = { filters: [], fields: [], error: null };

/** companyId -> a settled, SUCCESSFUL load. Read synchronously on render. */
const settledLoads = new Map();

/** companyId -> the load currently in flight, so two mounts share one. */
const pendingLoads = new Map();

/** `${companyId}|${rulesKey}` -> a decoded preview. Failures are not cached. */
const previewCache = new Map();

/** Mounted consumers, notified when a mutation invalidates the cache. */
const invalidationListeners = new Set();

/**
 * Bumped by every invalidation. A response that started before the bump must
 * not write its result into the caches afterwards — it describes the world as
 * it was before the create/update/delete that invalidated it.
 */
let cacheGeneration = 0;

/**
 * The cached load for a company, or null.
 *
 * Synchronous on purpose: a hook that can answer during render never renders a
 * spinner for an answer it already holds.
 *
 * @param {string|null} companyId
 * @returns {{filters: Object[], fields: Object[], error: string|null}|null}
 */
const readCachedLoad = (companyId) =>
  companyId === null ? null : settledLoads.get(companyId) || null;

/**
 * Fetch the saved filters and the field registry, at most once per company.
 *
 * Both requests are ISSUED before either is awaited. The builder needs the
 * registry to render a field dropdown AND the saved list to render the
 * sidebar, and neither depends on the other — sequencing them would make
 * opening the screen cost two round trips to show one form.
 *
 * @param {string} companyId
 * @returns {Promise<{filters: Object[], fields: Object[], error: string|null}>}
 */
const loadFiltersAndFields = (companyId) => {
  const inFlight = pendingLoads.get(companyId);
  if (inFlight) return inFlight;

  const generation = cacheGeneration;

  const promise = Promise.allSettled([
    listFilters(commsGet, { companyId }),
    listFilterFields(commsGet, { companyId }),
  ]).then(([filtersResult, fieldsResult]) => {
    // EITHER failure is a failed screen, unlike the list page where a dead
    // overview still leaves a usable list. Without the registry there is no
    // field to pick and the builder cannot render a row at all, so a
    // half-loaded builder would be a form that silently cannot be filled in.
    const failure = filtersResult.reason ?? fieldsResult.reason;

    const value = {
      filters:
        filtersResult.status === "fulfilled"
          ? filtersFromPayload(filtersResult.value).filters
          : [],
      fields:
        fieldsResult.status === "fulfilled"
          ? fieldsFromPayload(fieldsResult.value).fields
          : [],
      // Never `err.message` — `messageFor` is the only place that decides
      // what a status code says to a person.
      error: failure === undefined ? null : messageFor(failure),
    };

    if (generation === cacheGeneration) {
      pendingLoads.delete(companyId);
      // A failure is deliberately NOT cached: it would stick for the session.
      if (value.error === null) settledLoads.set(companyId, value);
    }

    return value;
  });

  pendingLoads.set(companyId, promise);
  return promise;
};

/**
 * Drop everything and tell every mounted consumer to load again.
 *
 * Called after a create/update/delete. One list on screen going stale because
 * a different picker saved a filter is exactly the bug a shared cache invites,
 * so the invalidation is global rather than per-hook.
 */
const invalidateFilterCache = () => {
  cacheGeneration += 1;
  settledLoads.clear();
  pendingLoads.clear();
  invalidationListeners.forEach((notify) => notify());
};

/* ── The saved filters and the field registry ─────────────────────────── */

/**
 * Everything the rule builder needs before it can render a single row.
 *
 * @returns {{
 *   filters: Object[],
 *   fields: Object[],
 *   loading: boolean,
 *   error: string|null,
 *   saving: boolean,
 *   reload: () => void,
 *   create: (body: Object) => Promise<Object|null>,
 *   update: (id: string, body: Object) => Promise<Object|null>,
 *   remove: (id: string) => Promise<boolean>,
 * }}
 */
export function useBroadcastFilters() {
  const { companyId, loading: companyLoading } = useCompanyId();

  /**
   * The last load this consumer saw settle. It is a FALLBACK, not the source
   * of truth: the cache is read first, and this is what keeps the previous
   * list on screen during a post-mutation refetch instead of blanking it.
   */
  const [lastLoad, setLastLoad] = useState(EMPTY_LOAD);
  /** Bumped when a mutation invalidates the cache from anywhere. */
  const [invalidations, setInvalidations] = useState(0);
  /**
   * A load is in flight for this consumer. Only ever consulted on a cold
   * cache — `loading` below is `!cached && pending` — so starting it true
   * costs nothing when the cache is warm and avoids a frame of "loaded but
   * empty" when it is not.
   */
  const [pending, setPending] = useState(true);
  const [mutationError, setMutationError] = useState(null);
  const [saving, setSaving] = useState(false);

  /**
   * The generation of the most recently STARTED load. Paired with the effect's
   * own `cancelled` flag: the flag says "my effect was replaced", the ref says
   * "someone newer is authoritative now". That second half matters here because
   * `create`/`update`/`remove` all trigger a reload from OUTSIDE the effect
   * body, so a response can outlive the render that asked for it.
   */
  const generationRef = useRef(0);

  // READ DURING RENDER. A warm cache answers before the first paint, so a
  // remount of `FilterPicker` shows the filters immediately rather than
  // flashing "Loading filters…" for one frame on the way to the same list.
  const cached = readCachedLoad(companyId);
  const load = cached || lastLoad;

  /** Re-subscribe once: an invalidation anywhere must reach every consumer. */
  useEffect(() => {
    const notify = () => setInvalidations((count) => count + 1);
    invalidationListeners.add(notify);
    return () => {
      invalidationListeners.delete(notify);
    };
  }, []);

  useEffect(() => {
    /*
     * Gate on the company id, for the reason written up in
     * `useBroadcastEstimate.js`: asking with a null one is a documented prior
     * bug where the backend answered with an empty result rather than an
     * error. Here that would render "no saved filters" to a company that has
     * five, and an empty field registry, which looks exactly like a backend
     * that does not support filters at all.
     */
    if (companyId === null) return undefined;

    // Already answered this session. Nothing to ask, nothing to wait for.
    if (readCachedLoad(companyId)) {
      setPending(false);
      return undefined;
    }

    let cancelled = false;
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    /** A response is worth believing only if it is both live and current. */
    const isStale = () => cancelled || generationRef.current !== generation;

    setPending(true);

    // Shared: a second consumer mounting mid-flight joins this promise rather
    // than issuing its own pair of requests.
    loadFiltersAndFields(companyId).then((value) => {
      if (isStale()) return;
      setLastLoad(value);
      setPending(false);
    });

    return () => {
      cancelled = true;
    };
  }, [companyId, invalidations]);

  const reload = useCallback(() => invalidateFilterCache(), []);

  const filters = load.filters;
  const fields = load.fields;
  // A mutation's complaint outranks the load's: it describes what the author
  // just tried to do, which is the thing they are waiting to hear about.
  const error = mutationError !== null ? mutationError : load.error;
  const loading = companyId === null ? companyLoading : !cached && pending;

  /**
   * Run a mutation, refresh the list on success, and surface a failure as copy.
   *
   * The three mutations differ only in the call they make and what they hand
   * back, so the saving flag, the error handling and the refresh live here
   * once. A failure deliberately does NOT reload: the list on screen is still
   * the last thing the server confirmed, and replacing it with a spinner
   * because a save failed loses the context the author needs to retry.
   */
  const mutate = useCallback(
    async (run, resultOf) => {
      setSaving(true);
      setMutationError(null);
      try {
        const response = await run();
        reload();
        return resultOf(response);
      } catch (err) {
        setMutationError(messageFor(err));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [reload]
  );

  const create = useCallback(
    (body) =>
      mutate(
        () => createFilter(commsPost, { companyId }, body),
        (response) => filterFromRecord(bodyOf(response))
      ),
    [companyId, mutate]
  );

  const update = useCallback(
    (id, body) =>
      mutate(
        () => updateFilter(commsPatch, id, body),
        (response) => filterFromRecord(bodyOf(response))
      ),
    [mutate]
  );

  const remove = useCallback(
    (id) => mutate(() => deleteFilter(commsRemove, id), () => true),
    [mutate]
  );

  return { filters, fields, loading, error, saving, reload, create, update, remove };
}

/* ── The live preview ─────────────────────────────────────────────────── */

/**
 * How many people the rules currently being edited would reach.
 *
 * @param {Array<{field: string, op: string, value: *}>} rules
 * @returns {{ preview: Object|null, previewing: boolean, error: string|null }}
 */
export function useFilterPreview(rules) {
  const { companyId, loading: companyLoading } = useCompanyId();

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState(null);

  /** Same pairing as above: liveness from the flag, recency from the ref. */
  const generationRef = useRef(0);

  /*
   * ---------------------------------------------------------------------
   * KEYED ON A SERIALIZATION OF THE RULES — the most important line here.
   * ---------------------------------------------------------------------
   * `rules` is a NEW ARRAY on every render, because the builder replaces it
   * immutably on every keystroke; depending on its identity would fire one
   * request per render whether or not anything changed. Reducing it to a
   * primitive gives the effect a dependency that only moves when the answer
   * could move.
   *
   * `JSON.stringify` is the right tool for exactly this data and no more. A
   * rule is `{field, op, value}` where value is a string, a number, a boolean
   * or an array of those — no dates, no undefined, no cycles, no class
   * instances, none of the cases where stringify quietly loses information.
   * It IS key-order sensitive, which is fine: the rules are built by one
   * component with one literal shape, so a reordered key would mean the same
   * object was constructed two different ways, which is not something that
   * happens here. The alternative — a hand-rolled deep compare in a ref — is
   * more code doing the same job with more places to be wrong.
   *
   * The request then re-parses this key rather than closing over `rules`, so
   * what goes on the wire is exactly what the dependency describes. There is
   * no arrangement in which the effect fires for one rule list and sends
   * another.
   */
  const ruleList = Array.isArray(rules) ? rules : [];
  const rulesKey = JSON.stringify(ruleList);
  const hasEnoughRules = ruleList.length >= MIN_FILTER_RULES;

  useEffect(() => {
    /*
     * THE HEADLINE: below the minimum, ask nothing at all.
     *
     * The generation bump is what makes this a real cancellation rather than
     * a UI reset — a request already in flight for the previous, longer rule
     * list will fail the recency check when it lands, so deleting the last
     * rule cannot be followed a moment later by a stale count appearing under
     * an empty form.
     */
    if (!hasEnoughRules) {
      generationRef.current += 1;
      setPreview(null);
      setError(null);
      setPreviewing(false);
      return undefined;
    }

    // Same company-id gate as every other comms hook. While it resolves this
    // is a PENDING preview, which `previewing` already means.
    if (companyId === null) {
      setPreviewing(companyLoading);
      return undefined;
    }

    /*
     * ALREADY COUNTED THIS EXACT RULE LIST. Answer from memory.
     *
     * `FilterPicker` previews the SELECTED saved filter, so clicking between
     * two filters to compare them used to re-POST both every time, and every
     * remount re-asked for a number the author had just been shown. The counts
     * come from a nightly rollup and carry their own `refreshedAt`, so the
     * answer cannot go stale inside a session in any way the UI is not already
     * labelling honestly.
     */
    const cacheKey = `${companyId}|${rulesKey}`;
    const cachedPreview = previewCache.get(cacheKey);
    if (cachedPreview) {
      setPreview(cachedPreview);
      setError(null);
      setPreviewing(false);
      return undefined;
    }

    let cancelled = false;
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    const isStale = () => cancelled || generationRef.current !== generation;

    setPreviewing(true);
    // Clear both up front. A count computed for a different rule list is worse
    // than no count, and `error` describes the LAST attempt — the one now
    // starting, not the one before it.
    setPreview(null);
    setError(null);

    const timer = setTimeout(() => {
      previewFilter(commsPost, { companyId, rules: JSON.parse(rulesKey) })
        .then((response) => {
          const decoded = previewFromPayload(bodyOf(response));
          // Cached even if this consumer has moved on: the answer is about the
          // rules, not about who asked.
          previewCache.set(cacheKey, decoded);
          if (isStale()) return;
          setPreview(decoded);
          setError(null);
          setPreviewing(false);
        })
        .catch((err) => {
          if (isStale()) return;
          // null, not zeros. See the header.
          setPreview(null);
          setError(messageFor(err));
          setPreviewing(false);
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [rulesKey, hasEnoughRules, companyId, companyLoading]);

  return { preview, previewing, error };
}

export default useBroadcastFilters;
